import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  Spin,
  Row,
  Col
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  LoginOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserTenant, getMainPageUrl } from '../utils/tenantUtils';


const { Title, Text } = Typography;

// Quote Rotator Widget Component
const QuoteRotator: React.FC = () => {
  const quotes = [
    {
      text: "The quality of your life is the quality of your relationships.",
      author: "Tony Robbins"
    },
    {
      text: "Success is not final, failure is not fatal: it is the courage to continue that counts.",
      author: "Winston Churchill"
    },
    {
      text: "The way to get started is to quit talking and begin doing.",
      author: "Walt Disney"
    },
    {
      text: "Innovation distinguishes between a leader and a follower.",
      author: "Steve Jobs"
    },
    {
      text: "The future belongs to those who believe in the beauty of their dreams.",
      author: "Eleanor Roosevelt"
    },
    {
      text: "It is during our darkest moments that we must focus to see the light.",
      author: "Aristotle"
    }
  ];

  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

        useEffect(() => {
          const interval = setInterval(() => {
            setIsVisible(false);
            setTimeout(() => {
              setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
              setIsVisible(true);
            }, 500);
          }, 10400);

          return () => clearInterval(interval);
        }, [quotes.length]);

  return (
    <div style={{ 
      textAlign: 'center', 
      marginTop: 32,
      padding: '20px 0',
      borderTop: '1px solid rgba(0,0,0,0.06)',
      minHeight: '80px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <Text style={{ 
        color: '#666',
        fontSize: '18px',
        fontWeight: '500',
        fontStyle: 'italic',
        lineHeight: '1.4',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.5s ease-in-out'
      }}>
        "{quotes[currentQuoteIndex].text}"
      </Text>
      <div style={{ 
        color: '#999',
        fontSize: '16px',
        marginTop: '6px',
        fontWeight: '400',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 0.5s ease-in-out 0.1s'
      }}>
        — {quotes[currentQuoteIndex].author}
      </div>
    </div>
  );
};

const CognitoLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login, setNewPassword, isAuthenticated } = useCognitoAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'login' | 'new-password'>('login');
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [session, setSession] = useState<string | undefined>('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const tenant = getUserTenant();
      const mainPageUrl = getMainPageUrl(tenant);
      navigate(mainPageUrl);
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (values: any) => {
    setLoading(true);
    setError('');
    
    try {
      const result = await login(values.username, values.password);
      
      if (result.success) {
        if (result.challenge === 'NEW_PASSWORD_REQUIRED' && result.session) {
          // Handle new password challenge
          setUsername(values.username);
          setSession(result.session);
          setStep('new-password');
        } else {
          // Direct login successful, redirect to appropriate main page
          const tenant = getUserTenant();
          const mainPageUrl = getMainPageUrl(tenant);
          navigate(mainPageUrl);
        }
      } else {
        setError('Credenciais inválidas. Tente novamente.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro no login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (values: any) => {
    if (values.new_password !== values.confirm_password) {
      setError('As palavras-passe não coincidem.');
      return;
    }

    if (!session) {
      setError('Sessão inválida para definir nova palavra-passe.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const success = await setNewPassword(session, values.new_password, username);
      
      if (success) {
        // Password set successfully, redirect to appropriate main page
        setError('');
        setTimeout(() => {
          const tenant = getUserTenant();
          const mainPageUrl = getMainPageUrl(tenant);
          navigate(mainPageUrl);
        }, 100);
      } else {
        setError('Erro ao definir nova palavra-passe. Tente novamente.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao definir nova palavra-passe. Tente novamente.';
      
      // If session is invalid, redirect back to login
      if (errorMessage.includes('Invalid session') || errorMessage.includes('session can only be used once')) {
        setError('Sessão expirada. Por favor, faça login novamente.');
        setTimeout(() => {
          setStep('login');
          setSession(undefined);
        }, 2000);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('login');
    setError('');
    setNewPasswordValue('');
    setConfirmPassword('');
    localStorage.removeItem('cognito_challenge');
    localStorage.removeItem('cognito_challenge_session');
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      width: '100%',
      maxWidth: '100vw',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundImage: 'url(/Backgroung_Login.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'scroll',
      position: 'relative',
      boxSizing: 'border-box'
    }}>
      {/* Overlay for better readability */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(2px)'
      }} />
      
      <Card style={{ 
        width: 588, 
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        borderRadius: '20px',
        border: 'none',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ textAlign: 'center', marginBottom: 45 }}>
          <div style={{
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img 
              src="/Logo.png" 
              alt="Logo" 
              style={{ 
                height: '80px', 
                width: 'auto',
                maxWidth: '200px'
              }} 
            />
          </div>
          <Text style={{ 
            color: '#666',
            fontSize: '22px',
            fontWeight: '500'
          }}>
            {step === 'login' && 'Iniciar Sessão'}
            {step === 'new-password' && 'Definir Nova Palavra-passe'}
          </Text>
        </div>

        {error && (
          <Alert 
            message={error} 
            type="error" 
            showIcon 
            style={{ 
              marginBottom: 28,
              borderRadius: '12px',
              border: 'none',
              background: 'rgba(255, 77, 79, 0.1)',
              fontSize: '16px'
            }}
          />
        )}

        {step === 'login' && (
          <Form
            name="login"
            onFinish={handleLogin}
            layout="vertical"
          >
            <Form.Item
              name="username"
              label={<span style={{ color: '#333', fontWeight: '500', fontSize: '18px' }}>E-mail</span>}
              rules={[
                { required: true, message: 'Por favor insira o e-mail!' }
              ]}
            >
              <Input 
                prefix={<UserOutlined style={{ color: '#1890ff', fontSize: '20px' }} />} 
                placeholder="E-mail"
                size="large"
                style={{
                  borderRadius: '12px',
                  border: '2px solid #f0f0f0',
                  padding: '20px 24px',
                  fontSize: '20px',
                  height: '68px',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1890ff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f0f0f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ color: '#333', fontWeight: '500', fontSize: '18px' }}>Palavra-passe</span>}
              rules={[{ required: true, message: 'Por favor insira a palavra-passe!' }]}
            >
              <Input.Password 
                prefix={<LockOutlined style={{ color: '#1890ff', fontSize: '20px' }} />} 
                placeholder="Palavra-passe"
                size="large"
                style={{
                  borderRadius: '12px',
                  border: '2px solid #f0f0f0',
                  padding: '20px 24px',
                  fontSize: '20px',
                  height: '68px',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1890ff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f0f0f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 45 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<LoginOutlined />}
                size="large"
                block
                style={{
                  height: '72px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                  border: 'none',
                  fontSize: '22px',
                  fontWeight: '600',
                  boxShadow: '0 8px 20px rgba(24, 144, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 25px rgba(24, 144, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(24, 144, 255, 0.3)';
                }}
              >
                Iniciar Sessão
              </Button>
            </Form.Item>
          </Form>
        )}

        {step === 'new-password' && (
          <div>
            <Alert
              message="Nova Palavra-passe Necessária"
              description="Por favor, defina uma nova palavra-passe para a sua conta."
              type="info"
              showIcon
              style={{ 
                marginBottom: 32,
                borderRadius: '12px',
                border: 'none',
                background: 'rgba(24, 144, 255, 0.1)',
                fontSize: '16px'
              }}
            />
            
            <Form
              name="new-password"
              onFinish={handleSetNewPassword}
              layout="vertical"
            >
              <Form.Item
                name="new_password"
                label={<span style={{ color: '#333', fontWeight: '500', fontSize: '18px' }}>Nova Palavra-passe</span>}
                rules={[
                  { required: true, message: 'Por favor insira a nova palavra-passe!' },
                  { min: 8, message: 'A palavra-passe deve ter pelo menos 8 caracteres!' },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const hasUpperCase = /[A-Z]/.test(value);
                      const hasLowerCase = /[a-z]/.test(value);
                      const hasNumber = /[0-9]/.test(value);
                      const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
                      
                      if (!hasUpperCase) {
                        return Promise.reject(new Error('A palavra-passe deve conter pelo menos uma letra maiúscula!'));
                      }
                      if (!hasLowerCase) {
                        return Promise.reject(new Error('A palavra-passe deve conter pelo menos uma letra minúscula!'));
                      }
                      if (!hasNumber) {
                        return Promise.reject(new Error('A palavra-passe deve conter pelo menos um número!'));
                      }
                      if (!hasSpecialChar) {
                        return Promise.reject(new Error('A palavra-passe deve conter pelo menos um carácter especial (!@#$%^&*...)!'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                help={
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '8px', lineHeight: '1.6' }}>
                    <div style={{ marginBottom: '4px' }}><strong>Requisitos da palavra-passe:</strong></div>
                    <div>• Mínimo de 8 caracteres</div>
                    <div>• Pelo menos uma letra maiúscula (A-Z)</div>
                    <div>• Pelo menos uma letra minúscula (a-z)</div>
                    <div>• Pelo menos um número (0-9)</div>
                    <div>• Pelo menos um carácter especial (!@#$%^&*...)</div>
                  </div>
                }
              >
                <Input.Password 
                  prefix={<LockOutlined style={{ color: '#1890ff', fontSize: '20px' }} />} 
                  placeholder="Nova palavra-passe"
                  size="large"
                  style={{
                    borderRadius: '12px',
                    border: '2px solid #f0f0f0',
                    padding: '20px 24px',
                    fontSize: '20px',
                    height: '68px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1890ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f0f0f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label={<span style={{ color: '#333', fontWeight: '500', fontSize: '18px' }}>Confirmar Palavra-passe</span>}
                rules={[
                  { required: true, message: 'Por favor confirme a palavra-passe!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('As palavras-passe não coincidem!'));
                    },
                  }),
                ]}
              >
                <Input.Password 
                  prefix={<LockOutlined style={{ color: '#1890ff', fontSize: '20px' }} />} 
                  placeholder="Confirmar palavra-passe"
                  size="large"
                  style={{
                    borderRadius: '12px',
                    border: '2px solid #f0f0f0',
                    padding: '20px 24px',
                    fontSize: '20px',
                    height: '68px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1890ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f0f0f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginTop: 45 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Button 
                      onClick={handleBackToLogin}
                      size="large"
                      block
                      style={{
                        height: '72px',
                        borderRadius: '12px',
                        border: '2px solid #f0f0f0',
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#666',
                        fontSize: '22px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#1890ff';
                        e.currentTarget.style.color = '#1890ff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#f0f0f0';
                        e.currentTarget.style.color = '#666';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      Voltar
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      loading={loading}
                      icon={<KeyOutlined />}
                      size="large"
                      block
                      style={{
                        height: '72px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                        border: 'none',
                        fontSize: '22px',
                        fontWeight: '600',
                        boxShadow: '0 8px 20px rgba(24, 144, 255, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 12px 25px rgba(24, 144, 255, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(24, 144, 255, 0.3)';
                      }}
                    >
                      Definir
                    </Button>
                  </Col>
                </Row>
              </Form.Item>
            </Form>
          </div>
        )}

        <QuoteRotator />
      </Card>
    </div>
  );
};

export default CognitoLogin;
